import { GroupOutlined, InfoOutlined, KeyboardArrowDown, StickyNote2Outlined } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import { Fragment, useMemo } from 'react'
import { useCatalogData } from '../CatalogList/useCatalogData'
import type { ManufacturerRow } from './ManufacturerList.types'
import { formatUrl, getUniqueAliasEntries } from './helpers'

export interface ManufacturerTableProps {
  loading: boolean
  rows: ManufacturerRow[]
  expandedById: Record<number, boolean>
  onToggleExpand: (id: number) => void
  onEditManufacturer: (id: number) => void
  onEditAlias: (manufacturerId: number, aliasIndex: number) => void
  onOpenAuthorizedOem: (manufacturer: ManufacturerRow) => void
  onOpenNotes: (rowId: number) => void
}

export function ManufacturerTable({
  loading,
  rows,
  expandedById,
  onToggleExpand,
  onEditManufacturer,
  onEditAlias,
  onOpenAuthorizedOem,
  onOpenNotes,
}: ManufacturerTableProps) {
  const { rows: catalogs } = useCatalogData()
  const contractVehicles = useMemo<Record<string, string | undefined>>(
    () =>
      Object.fromEntries(catalogs.map(contract => [contract.contractNumber.toLowerCase(), contract.contractVehicle])),
    [catalogs],
  )

  return (
    <Table stickyHeader color="primary">
      <TableHead>
        <TableRow>
          <TableCell width="20%">Manufacturers</TableCell>
          <TableCell width="15%">Alias</TableCell>
          <TableCell width="17%">Contract Aliases</TableCell>
          <TableCell width="10%">OEM Code</TableCell>
          <TableCell width="16%">URL</TableCell>
          <TableCell width="8%">
            <Box display="inline-flex" alignItems="center" gap={0.5}>
              Direct
              <Tooltip title="Direct relationship">
                <InfoOutlined fontSize="small" />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell width="8%">Authorized Suppliers</TableCell>
          <TableCell width="6%">Notes</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {loading
          ? Array.from({ length: 25 }).map((_, index) => (
              <TableRow key={index}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : rows.map((row: ManufacturerRow) => {
              const isExpanded = !!expandedById[row.id]

              return (
                <Fragment key={row.id}>
                  {/* Parent row */}
                  <TableRow hover sx={{ bgcolor: isExpanded ? '#eef1f4' : 'transparent' }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Link component="button" underline="hover" onClick={() => onEditManufacturer(row.id)}>
                          {row.supplier}
                        </Link>
                        <IconButton
                          size="small"
                          aria-label={isExpanded ? 'Collapse manufacturer' : 'Expand manufacturer'}
                          onClick={() => onToggleExpand(row.id)}
                        >
                          <KeyboardArrowDown
                            className="manufacturer-table-arrow-down"
                            sx={{
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>

                    <TableCell color="text.secondary">—</TableCell>
                    <TableCell>
                      {row.contractAliases.length > 0
                        ? row.contractAliases.map(entry => (
                            <div key={`${entry.contractNumber}:${entry.alias}`}>
                              <Box component="span" fontWeight={600}>
                                {contractVehicles[entry.contractNumber] ?? entry.contractNumber.toUpperCase()}
                              </Box>
                              : {entry.alias}
                            </div>
                          ))
                        : '—'}
                    </TableCell>
                    <TableCell>{row.mfrCodeUpper ?? '—'}</TableCell>

                    <TableCell>
                      {row.url ? (
                        <Link href={formatUrl(row.url) ?? undefined} target="_blank" rel="noopener noreferrer">
                          {row.url}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>

                    <TableCell>
                      <Checkbox size="small" checked={row.direct ?? false} disabled displayOnly />
                    </TableCell>

                    <TableCell>
                      <IconButton onClick={() => onOpenAuthorizedOem(row)}>
                        <GroupOutlined />
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <IconButton aria-label="Open notes" onClick={() => onOpenNotes(row.id)}>
                        <StickyNote2Outlined />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {isExpanded &&
                    getUniqueAliasEntries(row.aliases).map(({ alias, index }) => (
                      <TableRow key={`m:${row.id}|alias:${index}`} hover>
                        <TableCell>
                          <Box paddingLeft={3.5}>
                            <Link component="button" underline="hover" onClick={() => onEditAlias(row.id, index)}>
                              {alias}
                            </Link>
                          </Box>
                        </TableCell>

                        <TableCell>{alias}</TableCell>
                        <TableCell color="text.secondary">—</TableCell>
                        <TableCell color="text.secondary">—</TableCell>
                        <TableCell color="text.secondary">—</TableCell>

                        <TableCell>
                          <Checkbox size="small" disabled />
                        </TableCell>

                        <TableCell>
                          <IconButton aria-label="Open authorized suppliers" onClick={() => onOpenAuthorizedOem(row)}>
                            <GroupOutlined />
                          </IconButton>
                        </TableCell>

                        <TableCell />
                      </TableRow>
                    ))}
                </Fragment>
              )
            })}
      </TableBody>
    </Table>
  )
}
